import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, MenuItem } from '@mui/material';
import { Add as AddIcon, Save } from '@mui/icons-material';

import {
  CustomAutoComplete,
  CustomButton,
  CustomTextArea,
  CustomTextField,
  DrawerForm,
  DrawerFormActionSummary,
  DrawerFormActions,
  DrawerFormSection,
  DrawerFormSidebar,
  StatusChip,
} from '../../../common/components';
import { formatCurrency } from '../../../utils/formatUtils';
import {
  ACCOUNTING_DRAWER_WIDTH,
  ENTRY_DRAWER_SECTIONS,
} from '../constants/accountingEntriesConstants';
import {
  buildAccountingEntryPayload,
  createEmptyAccountingForm,
  emptyLine,
  extractAccountingErrorMessage,
  formatAccountingAmountInput,
  parseAccountingAmount,
  roundMoney,
  sumAccountingLines,
  validateAccountingEntryForm,
} from '../utils/accountingEntriesUtils';
import AccountingEntryLineRow from './AccountingEntryLineRow';

const AccountingEntryDrawer = ({ open, entry, onClose, onSave, isSaving }) => {
  const [form, setForm] = useState(createEmptyAccountingForm());
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState('datos');

  useEffect(() => {
    if (!open) return;
    if (entry) {
      setForm({
        entry_date: entry.entry_date || createEmptyAccountingForm().entry_date,
        description: entry.description || '',
        status: entry.status || 'draft',
        lines: (entry.lines || []).map((line) => ({
          account_label: line.account_label || '',
          debit_amount: formatAccountingAmountInput(line.debit_amount),
          credit_amount: formatAccountingAmountInput(line.credit_amount),
        })),
      });
    } else {
      setForm(createEmptyAccountingForm());
    }
    setError('');
    setActiveSection('datos');
  }, [open, entry]);

  const totals = useMemo(() => sumAccountingLines(form.lines), [form.lines]);
  const difference = roundMoney(totals.debit - totals.credit);

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleLineChange = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      lines: prev.lines.map((line, lineIndex) => {
        if (lineIndex !== index) return line;
        const next = { ...line, [field]: value };
        if (field === 'debit_amount' && parseAccountingAmount(value) > 0) next.credit_amount = '';
        if (field === 'credit_amount' && parseAccountingAmount(value) > 0) next.debit_amount = '';
        return next;
      }),
    }));
  };

  const addLine = () => {
    setForm((prev) => ({ ...prev, lines: [...prev.lines, emptyLine()] }));
  };

  const removeLine = (index) => {
    setForm((prev) => ({
      ...prev,
      lines: prev.lines.filter((_, lineIndex) => lineIndex !== index),
    }));
  };

  const submit = async () => {
    const validationError = validateAccountingEntryForm(form);
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      await onSave(buildAccountingEntryPayload(form));
      onClose();
    } catch (err) {
      setError(extractAccountingErrorMessage(err));
    }
  };

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'lineas':
        return (
          <DrawerFormSection
            title="Líneas contables"
            grid={false}
            action={(
              <CustomButton variant="outlined" startIcon={<AddIcon />} onClick={addLine}>
                Agregar línea
              </CustomButton>
            )}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
              {form.lines.map((line, index) => (
                <AccountingEntryLineRow
                  key={`line-${index}`}
                  line={line}
                  index={index}
                  canRemove={form.lines.length > 2}
                  onChange={handleLineChange}
                  onRemove={removeLine}
                />
              ))}
            </Box>
          </DrawerFormSection>
        );
      case 'resumen':
        return (
          <DrawerFormSection title="Resumen" grid={false}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                gap: 1.25,
              }}
            >
              <DrawerFormActionSummary label="Debe" value={formatCurrency(totals.debit, 2)} />
              <DrawerFormActionSummary label="Haber" value={formatCurrency(totals.credit, 2)} />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <StatusChip
                  size="small"
                  color={difference === 0 ? 'success' : 'warning'}
                  label={difference === 0 ? 'Balanceado' : `Diferencia ${formatCurrency(Math.abs(difference), 2)}`}
                />
              </Box>
            </Box>
          </DrawerFormSection>
        );
      case 'datos':
      default:
        return (
          <DrawerFormSection title="Datos del asiento">
            <CustomTextField
              label="Fecha"
              type="date"
              name="entry_date"
              value={form.entry_date}
              onChange={handleFieldChange}
              shrinkLabel
              sx={{ gridColumn: { xs: '1', sm: 'span 1', md: 'span 4' } }}
            />
            <CustomAutoComplete
              label="Estado"
              name="status"
              value={form.status}
              onChange={handleFieldChange}
              sx={{ gridColumn: { xs: '1', sm: 'span 1', md: 'span 4' } }}
            >
              <MenuItem value="draft">Borrador</MenuItem>
              <MenuItem value="posted">Contabilizado</MenuItem>
            </CustomAutoComplete>
            <CustomTextArea
              label="Descripción"
              name="description"
              value={form.description}
              onChange={handleFieldChange}
              minRows={3}
              maxRows={4}
              sx={{ gridColumn: { xs: '1', sm: '1 / -1', md: 'span 12' } }}
            />
          </DrawerFormSection>
        );
    }
  };

  return (
    <DrawerForm
      open={open}
      onClose={onClose}
      title={entry ? 'Editar asiento manual' : 'Nuevo asiento manual'}
      width={ACCOUNTING_DRAWER_WIDTH}
      sidebar={(
        <DrawerFormSidebar
          sections={ENTRY_DRAWER_SECTIONS}
          activeSection={activeSection}
          onSelect={setActiveSection}
        />
      )}
      footer={(
        <DrawerFormActions
          summary={(
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', minWidth: 0 }}>
              <DrawerFormActionSummary label="Debe" value={formatCurrency(totals.debit, 2)} />
              <DrawerFormActionSummary label="Haber" value={formatCurrency(totals.credit, 2)} />
              <StatusChip
                size="small"
                color={difference === 0 ? 'success' : 'warning'}
                label={difference === 0 ? 'Balanceado' : `Diferencia ${formatCurrency(Math.abs(difference), 2)}`}
              />
            </Box>
          )}
        >
          <CustomButton variant="outlined" onClick={onClose} disabled={isSaving}>Cancelar</CustomButton>
          <CustomButton variant="base" startIcon={<Save />} onClick={submit} isLoading={isSaving}>
            Guardar
          </CustomButton>
        </DrawerFormActions>
      )}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, md: 1.25 }, minWidth: 0 }}>
        {error && <Alert severity="error">{error}</Alert>}
        {renderActiveSection()}
      </Box>
    </DrawerForm>
  );
};

export default AccountingEntryDrawer;
